import type { FirestoreTimestamp } from './firebase'

export interface LinkedUser {
  uid: string
  displayName: string
  email: string
  photoURL: string | null
  phone: string
}

export interface EmergencyContact {
  name: string
  phone: string
  email: string
  relationship: string
}

export interface FacilityInfo {
  name: string
  contact: string
  teacher?: string
  therapist?: string
}

export interface SafetyProfile {
  id: string
  ownerUid?: string
  emoji: string
  firstName: string
  lastName: string
  fullName: string
  nickname: string
  age: number
  bloodType: string
  relationship: string
  conditions: string[]
  communicationLevel: string
  medicalNotes: string
  emergencyMessage: string
  emergencyContacts: EmergencyContact[]
  school?: FacilityInfo
  therapyCenter?: FacilityInfo
  sharedWithEmails: string[]
  linkedUids: string[]
  status: string
  photoUrl?: string
  showPhotoOnSOS?: boolean
  allergies?: string[]
  medications?: string[]
  behavioralNotes?: string
  passportEnabled?: boolean
  passportToken?: string
  /** Env-aware public passport URL from the server (BeaconPaths::url). */
  passportUrl?: string
  createdAt: FirestoreTimestamp
  updatedAt: FirestoreTimestamp
}

export type ProfileFormData = Omit<SafetyProfile, 'id' | 'createdAt' | 'updatedAt'>

export function emptyProfileForm(): ProfileFormData {
  return {
    emoji: '🧒',
    firstName: '',
    lastName: '',
    fullName: '',
    nickname: '',
    age: 0,
    bloodType: '',
    relationship: '',
    conditions: [],
    communicationLevel: '',
    medicalNotes: '',
    emergencyMessage: '',
    emergencyContacts: [{ name: '', phone: '', email: '', relationship: '' }],
    school: { name: '', contact: '', teacher: '' },
    therapyCenter: { name: '', contact: '', therapist: '' },
    sharedWithEmails: [],
    linkedUids: [],
    status: 'At Home',
    showPhotoOnSOS: false,
    allergies: [],
    medications: [],
    behavioralNotes: '',
    passportEnabled: false,
  }
}

export function normalizeProfile(id: string, data: Record<string, unknown>): SafetyProfile {
  const fullName = String(data.fullName || data.name || data.full_name || 'Unnamed')
  const parts = fullName.trim().split(' ')
  return {
    id,
    emoji: String(data.emoji || '🧒'),
    firstName: String(data.firstName || parts[0] || 'Unnamed'),
    lastName: String(data.lastName || parts.slice(1).join(' ') || ''),
    fullName,
    nickname: String(data.nickname || ''),
    age: Number(data.age) || 0,
    bloodType: String(data.bloodType || data.blood_type || ''),
    relationship: String(data.relationship || ''),
    conditions: Array.isArray(data.conditions) ? (data.conditions as string[]) : [],
    communicationLevel: String(data.communicationLevel || data.communication_level || ''),
    medicalNotes: String(data.medicalNotes || data.medical_notes || ''),
    emergencyMessage: String(data.emergencyMessage || data.emergency_message || ''),
    emergencyContacts: Array.isArray(data.emergencyContacts)
      ? (data.emergencyContacts as Record<string, unknown>[]).map((c) => ({
          name: String(c.name || ''),
          phone: String(c.phone || ''),
          email: String(c.email || ''),
          relationship: String(c.relationship || ''),
        }))
      : [],
    school: data.school ? (data.school as FacilityInfo) : undefined,
    therapyCenter: data.therapyCenter
      ? (data.therapyCenter as FacilityInfo)
      : data.therapy_center
        ? (data.therapy_center as FacilityInfo)
        : undefined,
    sharedWithEmails: Array.isArray(data.sharedWithEmails) ? (data.sharedWithEmails as string[]) : [],
    linkedUids: Array.isArray(data.linkedUids) ? (data.linkedUids as string[]) : [],
    status: data.status ? String(data.status) : '',
    photoUrl: data.photoUrl ? String(data.photoUrl) : undefined,
    showPhotoOnSOS: data.showPhotoOnSOS === true,
    allergies: Array.isArray(data.allergies) ? (data.allergies as string[]) : [],
    medications: Array.isArray(data.medications) ? (data.medications as string[]) : [],
    behavioralNotes: data.behavioralNotes ? String(data.behavioralNotes) : undefined,
    passportEnabled: data.passportEnabled === true,
    passportToken: data.passportToken ? String(data.passportToken) : undefined,
    passportUrl: data.passportUrl ? String(data.passportUrl) : undefined,
    createdAt: (data.createdAt as FirestoreTimestamp) || null,
    updatedAt: (data.updatedAt as FirestoreTimestamp) || null,
  }
}

export function conditionColor(condition: string): string {
  const lower = (condition || '').toLowerCase()
  if (lower.includes('autism') || lower.includes('asd')) return 'bg-success-100 text-success-700 border border-success-300'
  if (lower.includes('gdd') || lower.includes('intellectual')) return 'bg-purple-100 text-purple-700 border border-purple-300'
  if (lower.includes('sensory') || lower.includes('spd')) return 'bg-warning-100 text-warning-700 border border-warning-300'
  if (lower.includes('adhd')) return 'bg-orange-100 text-orange-700 border border-orange-300'
  if (lower.includes('down')) return 'bg-settings-100 text-settings-700 border border-settings-300'
  if (lower.includes('cerebral')) return 'bg-rose-100 text-rose-700 border border-rose-300'
  return 'bg-gray-100 text-gray-700 border border-gray-300'
}
